<template>
  <div class="g-page">
    <div class="content-container">
      <!-- HEADER -->
      <div class="header-row">
        <h2 class="page-title">
          <b-icon icon="boxes" class="title-icon"></b-icon>
          Mahsulotlar Hisoboti
        </h2>
        <div class="header-actions">
          <button class="refresh-btn" @click="loadData()" :title="'Yangilash'">
            <b-icon icon="arrow-repeat"></b-icon>
          </button>
        </div>
      </div>

      <!-- FILTER PANEL -->
      <div class="filter-panel">
        <div class="filter-grid">
          <div class="filter-cell f-search">
            <label>Qidiruv</label>
            <div class="search-wrapper">
              <b-icon icon="search" class="search-icon"></b-icon>
              <input v-model="filters.search" type="search" placeholder="Mahsulot nomi yoki kodi..." class="form-input" @input="onSearch" />
            </div>
          </div>

          <div class="filter-cell">
            <label>Sklad</label>
            <select v-model="filters.sklad_id" class="form-input" @change="loadData()">
              <option value="">Joriy sklad</option>
              <option value="all">Barcha skladlar</option>
              <option v-for="s in sklads" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>

          <div class="filter-cell">
            <label>Tartiblash</label>
            <select v-model="filters.sort" class="form-input" @change="loadData()">
              <option value="name">Nomi (A-Z)</option>
              <option value="balance">Qoldiq (ko'p-kam)</option>
              <option value="balance_asc">Qoldiq (kam-ko'p)</option>
              <option value="profit">Foyda (ko'p-kam)</option>
              <option value="cost">Tannarx (ko'p-kam)</option>
              <option value="retail">Sotish (ko'p-kam)</option>
            </select>
          </div>

          <!-- KAM QOLDIQ -->
          <div class="filter-cell">
            <label>Kam qoldiq</label>
            <div class="low-stock-row">
              <label class="ls-toggle">
                <input type="checkbox" v-model="filters.low_stock" @change="loadData()" />
                <span>Faqat kam</span>
              </label>
              <input
                type="number"
                v-model.number="filters.threshold"
                min="0"
                step="1"
                class="form-input ls-threshold"
                @change="loadData()"
                title="Threshold (default 10)"
              />
              <span class="ls-unit">≤</span>
            </div>
          </div>

        </div>
      </div>

      <!-- TOTALS — barcha filterlangan mahsulotlar (pagination'siz) -->
      <div v-if="totals" class="totals-row">
        <div class="t-card primary">
          <div class="t-label">Jami mahsulot</div>
          <div class="t-value">{{ formatNum(totals.products_count) }}</div>
          <div class="t-sub">turdagi mahsulot</div>
        </div>
        <div class="t-card info">
          <div class="t-label">Jami birlik</div>
          <div class="t-value">{{ formatNum(totals.total_qty) }}</div>
          <div class="t-sub">dona/kg/litr (jami)</div>
        </div>
        <div class="t-card warning">
          <div class="t-label">Jami tannarx</div>
          <div class="t-value">{{ formatMoney(totals.total_cost) }}</div>
          <div class="t-sub">to'plangan kapital</div>
        </div>
        <div class="t-card cyan">
          <div class="t-label">Jami sotish</div>
          <div class="t-value">{{ formatMoney(totals.total_retail) }}</div>
          <div class="t-sub">chakana narxda</div>
        </div>
        <div class="t-card success">
          <div class="t-label">Potensial foyda</div>
          <div class="t-value">{{ formatMoney(totals.total_profit) }}</div>
          <div class="t-sub">{{ totals.profit_pct }}% margin</div>
        </div>
        <div v-if="totals.low_stock_count > 0" class="t-card danger">
          <div class="t-label">Kam qoldiq</div>
          <div class="t-value">{{ totals.low_stock_count }}</div>
          <div class="t-sub">{{ filters.threshold }} dan kam</div>
        </div>
      </div>

      <!-- Sahifa subtotali (kichikroq, alohida qator) -->
      <div v-if="totals && items.length > 0" class="page-totals">
        <span>📄 Joriy sahifa: <strong>{{ items.length }}</strong> ta mahsulot</span>
        <span>· Birlik: <strong>{{ formatNum(totals.page_qty) }}</strong></span>
        <span>· Tannarx: <strong>{{ formatMoney(totals.page_cost) }}</strong></span>
        <span>· Sotish: <strong>{{ formatMoney(totals.page_retail) }}</strong></span>
        <span>· Foyda: <strong>{{ formatMoney(totals.page_profit) }}</strong></span>
      </div>

      <!-- TABLE -->
      <div class="table-card">
        <div class="table-wrapper">
          <table class="custom-table">
            <thead>
              <tr>
                <th style="width:50px">#</th>
                <th>Mahsulot</th>
                <th class="num">Qoldiq</th>
                <th class="num">Tannarx</th>
                <th class="num">Chakana</th>
                <th class="num">Foyda/dona</th>
                <th class="num">%</th>
                <th class="num">Jami tannarx</th>
                <th class="num">Jami sotish</th>
                <th class="num">Jami foyda</th>
                <th style="width:60px;text-align:center">Amal</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(p, i) in items" :key="p.id" :class="{ 'row-low': p.low_stock }">
                <td>{{ offset + i + 1 }}</td>
                <td class="name-cell">
                  <div class="prod-name">
                    {{ p.name }}
                    <span v-if="p.low_stock" class="low-badge">⚠️ KAM</span>
                  </div>
                  <div class="prod-extra">{{ p.extra_name }} · #{{ p.number }}</div>
                </td>
                <td class="num">
                  <span class="balance-badge" :class="balClass(p.balance)">
                    {{ formatNum(p.balance) }}
                    <small v-if="p.unit_name">{{ p.unit_name }}</small>
                  </span>
                </td>
                <td class="num muted">{{ formatMoney(p.body_price) }}</td>
                <td class="num">{{ formatMoney(p.chakana_price) }}</td>
                <td class="num">
                  <span :class="profitClass(p.profit_per_unit)">
                    {{ formatMoney(p.profit_per_unit) }}
                  </span>
                </td>
                <td class="num">
                  <span :class="profitPctClass(p.profit_pct)">{{ p.profit_pct }}%</span>
                </td>
                <td class="num bold money muted">{{ formatMoney(p.total_cost) }}</td>
                <td class="num bold money">{{ formatMoney(p.total_retail) }}</td>
                <td class="num bold money">
                  <span :class="profitClass(p.total_profit)">{{ formatMoney(p.total_profit) }}</span>
                </td>
                <td style="text-align:center">
                  <button class="fix-btn" @click="openFixCost(p)" title="Tannarxni to'g'rilash">
                    <b-icon icon="pencil-square"></b-icon>
                  </button>
                </td>
              </tr>
              <tr v-if="loading">
                <td colspan="11" class="loading-row"><div class="spinner"></div> Yuklanmoqda...</td>
              </tr>
              <tr v-if="!loading && items.length === 0">
                <td colspan="11" class="empty-row">Mahsulot topilmadi</td>
              </tr>
            </tbody>
            <tfoot v-if="totals && items.length > 0">
              <tr class="footer-row">
                <td colspan="2">JAMI ({{ totals.products_count }} ta)</td>
                <td class="num bold">{{ formatNum(totals.total_qty) }}</td>
                <td colspan="4"></td>
                <td class="num bold money muted">{{ formatMoney(totals.total_cost) }}</td>
                <td class="num bold money">{{ formatMoney(totals.total_retail) }}</td>
                <td class="num bold money positive">{{ formatMoney(totals.total_profit) }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- INFINITE SCROLL — sentinel + load more -->
        <div ref="sentinel" class="infinite-sentinel"></div>
        <div v-if="loadingMore" class="loading-more">
          <div class="spinner"></div> Yana yuklanmoqda...
        </div>
        <div v-if="pagination.total > 0" class="scroll-info">
          <span>Ko'rsatildi: <b>{{ items.length }}</b> / <b>{{ pagination.total }}</b> ta</span>
          <button v-if="hasMore && !loadingMore" class="load-more-btn" @click="loadMore">
            Yana {{ Math.min(limit, pagination.total - items.length) }} ta yuklash
          </button>
          <span v-if="!hasMore && items.length > 0" class="all-loaded">✓ Hammasi yuklandi</span>
        </div>
      </div>
    </div>

    <!-- ═══════ TANNARXNI TO'G'RILASH MODAL ═══════ -->
    <b-modal id="modal-fix-cost" hide-footer no-close-on-backdrop modal-class="modern-modal"
             title="🛠 Tannarxni to'g'rilash">
      <div v-if="fixLoading" class="text-center" style="padding:30px">
        <b-spinner variant="primary"></b-spinner>
        <div class="mt-2 text-muted">Yuklanmoqda...</div>
      </div>

      <div v-else-if="fixCtx" class="fix-body">
        <div class="fix-head">
          <div class="fix-product">{{ fixCtx.product_name }}</div>
          <div class="fix-sub">{{ fixCtx.extra_name }} · #{{ fixCtx.product_number }}</div>
          <div class="fix-meta">
            <span v-if="fixCtx.all_sklad">Barcha skladlar ({{ fixCtx.sklad_count }} ta narx kartasi)</span>
            <span v-else>Tanlangan sklad narx kartasi</span>
            · Chakana {{ formatMoney(fixCtx.chakana_price) }}/birlik
          </div>
        </div>

        <div class="fix-current">
          <span class="fix-label">Hozirgi birlik tannarx</span>
          <span class="fix-current-val">{{ formatMoney(fixCtx.current_unit_cost_sum) }}</span>
          <span v-if="fixCtx.cost_currency === 'USD'" class="fix-raw">
            (xom: {{ fixCtx.current_body_price }} $ × kurs {{ formatMoney(fixCtx.dollar_rate) }})
          </span>
        </div>

        <div class="fix-input-block">
          <label>To'g'ri birlik tannarx</label>

          <!-- Kiritish valyutasi: men qaysida yozaman -->
          <div class="cur-row">
            <span class="cur-row-label">Kiritish:</span>
            <div class="cur-toggle">
              <button type="button" class="cur-btn" :class="{ active: fixInputCur === 1 }" @click="fixInputCur = 1">so'm</button>
              <button type="button" class="cur-btn" :class="{ active: fixInputCur === 2 }" @click="fixInputCur = 2">$ dollar</button>
            </div>
          </div>

          <input type="number" v-model.number="fixNewCost" class="form-input" min="0"
                 :placeholder="fixInputCur === 2 ? 'dollar (masalan 7.2)' : 'so\'m'" />

          <!-- Saqlash valyutasi: bazaga qaysida yozilsin -->
          <div class="cur-row">
            <span class="cur-row-label">Saqlash:</span>
            <div class="cur-toggle">
              <button type="button" class="cur-btn" :class="{ active: fixStoreCur === 1 }" @click="fixStoreCur = 1">so'm</button>
              <button type="button" class="cur-btn" :class="{ active: fixStoreCur === 2 }" @click="fixStoreCur = 2">$ dollar</button>
            </div>
          </div>

          <!-- Natija: nima saqlanadi + so'mdagi ekvivalenti -->
          <div v-if="fixNewCost > 0 && fixCtx.dollar_rate > 0" class="fix-hint-sum">
            → Saqlanadi: <b>{{ fixStoreCur === 2 ? (fixStoreRaw + ' $') : formatMoney(fixStoreRaw) }}</b>
            <span class="fix-hint-sub">≈ {{ formatMoney(fixNewUnitCostSum) }} (kurs {{ formatMoney(fixCtx.dollar_rate) }})</span>
          </div>

          <div v-if="fixStoreCur !== currentCurrencyCode" class="fix-hint-cur">
            ⚠️ Valyuta o'zgartirilmoqda: {{ currentCurrencyCode === 2 ? 'dollar' : "so'm" }} → {{ fixStoreCur === 2 ? 'dollar' : "so'm" }}
          </div>
        </div>

        <div v-if="fixNewCost !== '' && fixNewCost >= 0" class="fix-preview">
          <div class="fp-cell">
            <span class="fp-label">Yangi birlik foyda</span>
            <span class="fp-val" :class="profitClass(fixNewUnitProfit)">{{ formatMoney(fixNewUnitProfit) }}</span>
          </div>
          <div class="fp-cell">
            <span class="fp-label">Yangi foyda %</span>
            <span class="fp-val" :class="profitPctClass(fixNewProfitPct)">{{ fixNewProfitPct }}%</span>
          </div>
        </div>

        <div v-if="!fixCtx.all_sklad" class="fix-note">
          ℹ️ Faqat tanlangan skladning oxirgi narx kartasi yangilanadi.
        </div>
        <div v-else class="fix-note warn">
          ⚠️ Barcha skladlar ({{ fixCtx.sklad_count }} ta) tannarxi bir vaqtda yangilanadi.
          Aniq bitta skladni tuzatish uchun yuqorida sklad tanlang.
        </div>

        <div v-if="fixError" class="fix-error">⚠️ {{ fixError }}</div>
        <div v-if="fixResult" class="fix-done">
          ✅ Tuzatildi: {{ fixResult.updated }} ta narx kartasi yangilandi. Hisobot yangilandi.
        </div>

        <div class="fix-actions">
          <button class="btn-secondary" @click="$bvModal.hide('modal-fix-cost')">Yopish</button>
          <button v-if="!fixResult" class="btn-apply"
                  :disabled="fixApplying || fixNewCost === '' || fixNewCost < 0 || !fixCtx.has_price_card"
                  @click="applyFixCost()">
            <b-icon :icon="fixApplying ? 'arrow-repeat' : 'check-all'"></b-icon>
            {{ fixApplying ? 'Saqlanmoqda...' : 'Tannarxni saqlash' }}
          </button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';

export default {
  name: 'ProductsInventoryReport',
  data() {
    return {
      loading: false,
      loadingMore: false,
      sklads: [],
      filters: {
        search: '',
        sklad_id: '',
        sort: 'name',
        low_stock: false,
        threshold: 10,
      },
      items: [],
      totals: null,
      pagination: { total: 0, page: 1 },
      limit: 50,                  // Infinite scroll: 50 tadan
      offset: 0,
      observer: null,             // IntersectionObserver

      // Tannarxni to'g'rilash
      fixCtx: null,
      fixNewCost: '',
      fixInputCur: 1,           // kiritish valyutasi: 1=so'm, 2=dollar
      fixStoreCur: 1,           // saqlash valyutasi: 1=so'm, 2=dollar
      fixLoading: false,
      fixApplying: false,
      fixResult: null,
      fixError: '',
    };
  },
  computed: {
    hasMore() {
      return this.items.length < this.pagination.total;
    },
    // Mahsulotning HOZIRGI saqlangan valyuta kodi (1=so'm, 2=dollar)
    currentCurrencyCode() {
      return this.fixCtx && this.fixCtx.cost_currency === 'USD' ? 2 : 1;
    },
    // Kiritilgan qiymatning so'mdagi ekvivalenti (foyda hisobi shu — saqlash valyutasidan mustaqil)
    fixInputInSum() {
      if (!this.fixCtx) return 0;
      const v = parseFloat(this.fixNewCost) || 0;
      return this.fixInputCur === 2 ? v * (this.fixCtx.dollar_rate || 1) : v;
    },
    // Bazaga saqlanadigan XOM qiymat (saqlash valyutasida)
    fixStoreRaw() {
      if (!this.fixCtx) return 0;
      if (this.fixStoreCur === 2) {
        const rate = this.fixCtx.dollar_rate || 0;
        return rate > 0 ? Math.round((this.fixInputInSum / rate) * 100) / 100 : 0;
      }
      return Math.round(this.fixInputInSum);
    },
    fixNewUnitCostSum() {
      return this.fixInputInSum;
    },
    fixNewUnitProfit() {
      if (!this.fixCtx) return 0;
      return (this.fixCtx.chakana_price || 0) - this.fixNewUnitCostSum;
    },
    fixNewProfitPct() {
      const cost = this.fixNewUnitCostSum;
      if (cost <= 0) return 0;
      return Math.round((this.fixNewUnitProfit / cost) * 10000) / 100;
    },
  },
  methods: {
    formatMoney(n) {
      const v = parseFloat(n || 0);
      return v.toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + " so'm";
    },
    formatNum(n) {
      const v = parseFloat(n || 0);
      return v.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
    },
    balClass(b) {
      const v = parseFloat(b || 0);
      if (v <= this.filters.threshold) return 'bal-low';
      if (v <= this.filters.threshold * 3) return 'bal-mid';
      return 'bal-good';
    },
    profitClass(p) {
      const v = parseFloat(p || 0);
      if (v > 0) return 'positive';
      if (v < 0) return 'negative';
      return 'muted';
    },
    profitPctClass(p) {
      const v = parseFloat(p || 0);
      if (v >= 30) return 'pct-high';
      if (v >= 10) return 'pct-mid';
      if (v > 0)   return 'pct-low';
      return 'pct-bad';
    },

    onSearch: debounce(function () {
      this.reset();
    }, 350),

    /** Filter o'zgartirilganda boshidan yuklash */
    async reset() {
      this.offset = 0;
      this.items = [];
      await this.loadData(false);
    },

    /** Asosiy yuklash — append=true bo'lsa qo'shadi (infinite scroll), aks holda almashtiradi */
    async loadData(append = false) {
      if (append) this.loadingMore = true;
      else        this.loading = true;
      try {
        const params = {
          search: this.filters.search || undefined,
          sort: this.filters.sort,
          low_stock: this.filters.low_stock ? 1 : 0,
          threshold: this.filters.threshold,
          limit: this.limit,
          offset: this.offset,
        };
        if (this.filters.sklad_id) params.sklad_id = this.filters.sklad_id;
        const res = await axios.get('api/v1/report/products-inventory', { params });
        const newItems = res.data.items || [];

        if (append) this.items.push(...newItems);
        else        this.items = newItems;

        this.totals = res.data.totals || null;
        this.pagination = res.data.pagination || { total: 0, page: 1 };
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    /** Infinite scroll: pastga yetganda chaqirildi */
    async loadMore() {
      if (this.loadingMore || !this.hasMore) return;
      this.offset += this.limit;
      await this.loadData(true);
    },

    /** IntersectionObserver — sentinel ko'rinsa loadMore */
    setupObserver() {
      if (this.observer) this.observer.disconnect();
      this.$nextTick(() => {
        const sentinel = this.$refs.sentinel;
        if (!sentinel) return;
        this.observer = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) this.loadMore();
        }, { threshold: 0.1, rootMargin: '200px' });
        this.observer.observe(sentinel);
      });
    },

    loadSklads() {
      axios.get('api/v1/sklads').then(r => { this.sklads = r.data || []; }).catch(() => {});
    },

    // ════════ TANNARXNI TO'G'RILASH ════════
    errMsg(e) {
      return (e.response && e.response.data && (e.response.data.message || e.response.data.error)) || e.message || 'Xatolik';
    },
    async openFixCost(p) {
      this.fixCtx = null;
      this.fixNewCost = '';
      this.fixInputCur = 1;
      this.fixStoreCur = 1;
      this.fixResult = null;
      this.fixError = '';
      this.$bvModal.show('modal-fix-cost');
      this.fixLoading = true;
      try {
        const payload = { product_id: p.id, mode: 'preview' };
        if (this.filters.sklad_id) payload.sklad_id = this.filters.sklad_id;
        const res = await axios.post('api/v1/report/fix-product-cost', payload);
        this.fixCtx = res.data.line || null;
        // Tanlagichlarni mahsulotning joriy valyutasiga moslaymiz (1=so'm, 2=dollar)
        const cur = (this.fixCtx && this.fixCtx.cost_currency === 'USD') ? 2 : 1;
        this.fixInputCur = cur;
        this.fixStoreCur = cur;
      } catch (e) {
        this.fixError = this.errMsg(e);
      } finally {
        this.fixLoading = false;
      }
    },
    async applyFixCost() {
      if (!this.fixCtx || this.fixNewCost === '' || this.fixNewCost < 0) return;
      if (this.fixStoreCur === 2 && !(this.fixCtx.dollar_rate > 0)) {
        this.fixError = "Dollar kursi yo'q — dollarda saqlab bo'lmaydi";
        return;
      }
      const storeText = this.fixStoreCur === 2 ? (this.fixStoreRaw + ' $') : this.formatMoney(this.fixStoreRaw);
      const scopeText = this.fixCtx.all_sklad
        ? `Barcha skladlar (${this.fixCtx.sklad_count} ta)`
        : 'Tanlangan sklad';
      if (!confirm(`${scopeText} tannarxi ${storeText} (≈ ${this.formatMoney(this.fixNewUnitCostSum)}) ga o'zgartiriladi.\n\nDavom etaylikmi?`)) return;
      this.fixApplying = true;
      this.fixError = '';
      try {
        const payload = {
          product_id: this.fixCtx.product_id,
          mode: 'apply',
          new_cost: this.fixStoreRaw,          // saqlash valyutasidagi xom qiymat
          cost_currency: this.fixStoreCur,     // 1=so'm, 2=dollar
        };
        if (this.filters.sklad_id) payload.sklad_id = this.filters.sklad_id;
        const res = await axios.post('api/v1/report/fix-product-cost', payload);
        this.fixResult = res.data || null;
        await this.reset();   // hisobotni boshidan yangilash
      } catch (e) {
        this.fixError = this.errMsg(e);
      } finally {
        this.fixApplying = false;
      }
    },
  },
  watch: {
    'filters.sklad_id'() { this.reset(); },
    'filters.sort'()      { this.reset(); },
    'filters.low_stock'() { this.reset(); },
    'filters.threshold'() { this.reset(); },
  },
  async mounted() {
    this.loadSklads();
    await this.loadData(false);
    this.setupObserver();
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
  },
};
</script>

<style scoped>
.g-page { padding: 20px; height: 100%; overflow-y: auto; background: #f8fafc; }
.content-container { max-width: 1500px; margin: 0 auto; }

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
  gap: 12px;
}
.page-title {
  display: flex; align-items: center; gap: 10px;
  font-size: 22px; font-weight: 800; color: #1e293b; margin: 0;
}
.title-icon { color: #2563eb; font-size: 24px; }

.refresh-btn {
  background: white;
  border: 1px solid #e2e8f0;
  width: 40px; height: 40px;
  border-radius: 12px;
  cursor: pointer;
  color: #2563eb;
  display: flex; align-items: center; justify-content: center;
}
.refresh-btn:hover { background: #eff6ff; }

/* FILTER */
.filter-panel {
  background: white;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  padding: 14px;
  margin-bottom: 12px;
}
.filter-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.2fr;
  gap: 10px;
}
@media (max-width: 1100px) {
  .filter-grid { grid-template-columns: 1fr 1fr; }
}
.filter-cell label {
  display: block;
  font-size: 11px; font-weight: 700;
  color: #94a3b8; margin-bottom: 6px;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.search-wrapper { position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 12px; color: #94a3b8; font-size: 14px; }
.search-wrapper .form-input { padding-left: 36px; }

.form-input {
  width: 100%;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 9px 12px;
  font-size: 14px;
  color: #1e293b;
  outline: none;
  transition: 0.2s;
}
.form-input:focus {
  background: white;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37,99,235,0.08);
}

.low-stock-row {
  display: flex;
  align-items: center;
  gap: 6px;
}
.ls-toggle {
  display: flex; align-items: center; gap: 6px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  padding: 8px 10px;
  cursor: pointer;
  font-weight: 700;
  color: #b45309;
  font-size: 12px;
  white-space: nowrap;
  margin: 0;
}
.ls-toggle input { margin: 0; }
.ls-threshold { width: 70px; }
.ls-unit { color: #94a3b8; font-weight: 700; }

/* TOTALS */
.totals-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}
.t-card {
  background: white;
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  border-left: 4px solid #94a3b8;
}
.t-card.primary { border-color: #2563eb; }
.t-card.info    { border-color: #06b6d4; }
.t-card.warning { border-color: #f59e0b; }
.t-card.cyan    { border-color: #0891b2; }
.t-card.success { border-color: #16a34a; }
.t-card.danger  { border-color: #ef4444; background: #fef2f2; }

.t-label { font-size: 11px; color: #94a3b8; font-weight: 700; text-transform: uppercase; }
.t-value { font-size: 19px; font-weight: 800; color: #1e293b; margin: 4px 0 2px 0; font-family: 'Inter', system-ui; }
.t-sub   { font-size: 11px; color: #64748b; }

/* Sahifa subtotali — kichik chiziq */
.page-totals {
  background: #f1f5f9;
  border-radius: 10px;
  padding: 8px 14px;
  margin-bottom: 12px;
  font-size: 12px;
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}
.page-totals strong { color: #1e293b; font-weight: 800; }

/* TABLE */
.table-card {
  background: white;
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
  overflow: hidden;
}
.table-wrapper {
  overflow-x: auto;
  max-height: calc(100vh - 380px);
  overflow-y: auto;
}
.custom-table { width: 100%; border-collapse: collapse; }
.custom-table thead { position: sticky; top: 0; z-index: 2; }
.custom-table th {
  background: #f8fafc;
  padding: 11px 12px;
  color: #94a3b8;
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 1px solid #f1f5f9;
  white-space: nowrap; text-align: left;
}
.custom-table th.num,
.custom-table td.num { text-align: right; }
.custom-table td {
  padding: 9px 12px;
  border-bottom: 1px solid #f8fafc;
  font-size: 13px;
  color: #334155;
  vertical-align: middle;
}

.row-low td { background: #fef2f2 !important; }

.name-cell .prod-name {
  font-weight: 700; color: #1e293b;
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
}
.name-cell .prod-extra { font-size: 11px; color: #94a3b8; margin-top: 2px; }

.low-badge {
  background: #fee2e2; color: #b91c1c;
  padding: 1px 7px;
  border-radius: 8px;
  font-size: 9px; font-weight: 800;
  letter-spacing: 0.5px;
}

.balance-badge {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 800;
  font-family: 'Inter', system-ui;
}
.balance-badge small { font-size: 10px; font-weight: 600; opacity: 0.7; }
.bal-low  { background: #fee2e2; color: #b91c1c; }
.bal-mid  { background: #fef3c7; color: #b45309; }
.bal-good { background: #dcfce7; color: #15803d; }

.muted { color: #94a3b8; }
.bold { font-weight: 800; color: #1e293b; }
.money { font-family: 'Inter', system-ui; }

.positive { color: #16a34a; font-weight: 800; }
.negative { color: #dc2626; font-weight: 800; }

.pct-high { color: #15803d; font-weight: 800; }
.pct-mid  { color: #2563eb; font-weight: 700; }
.pct-low  { color: #b45309; font-weight: 700; }
.pct-bad  { color: #b91c1c; font-weight: 800; }

.footer-row td {
  background: #f8fafc !important;
  border-top: 2px solid #cbd5e1 !important;
  font-weight: 800;
  font-size: 13px;
  padding: 13px 12px !important;
}

.empty-row {
  text-align: center;
  padding: 50px !important;
  color: #94a3b8;
  font-style: italic;
}
.loading-row { text-align: center; padding: 30px; color: #2563eb; }

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #dbeafe;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
  margin-right: 8px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* INFINITE SCROLL */
.infinite-sentinel {
  height: 1px;
  margin: -1px 0;
}
.loading-more {
  text-align: center;
  padding: 18px;
  color: #2563eb;
  font-weight: 600;
  font-size: 13px;
  border-top: 1px solid #f1f5f9;
}
.scroll-info {
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px 18px;
  border-top: 1px solid #f1f5f9;
  font-size: 13px; color: #64748b;
}
.load-more-btn {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  padding: 8px 18px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: 0.2s;
}
.load-more-btn:hover {
  background: #2563eb;
  color: white;
}
.all-loaded {
  color: #16a34a;
  font-weight: 700;
  background: #dcfce7;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 12px;
}

/* ══════ TANNARXNI TO'G'RILASH ══════ */
.fix-btn {
  background: #fff7ed; border: 1px solid #fed7aa;
  width: 30px; height: 30px; border-radius: 8px;
  cursor: pointer; color: #c2410c;
}
.fix-btn:hover { background: #c2410c; color: white; border-color: #c2410c; }

.fix-body { padding: 4px 2px; }
.fix-head { margin-bottom: 12px; }
.fix-product { font-size: 16px; font-weight: 800; color: #1e293b; }
.fix-sub { font-size: 12px; color: #94a3b8; margin-top: 2px; }
.fix-meta { font-size: 12px; color: #64748b; margin-top: 6px; }

.fix-current {
  background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px;
  padding: 12px 14px; margin-bottom: 14px;
  display: flex; flex-direction: column; gap: 2px;
}
.fix-label { font-size: 11px; font-weight: 700; color: #b91c1c; text-transform: uppercase; letter-spacing: 0.4px; }
.fix-current-val { font-size: 20px; font-weight: 800; color: #b91c1c; font-family: 'Inter', system-ui; }
.fix-raw { font-size: 11px; color: #94a3b8; }

.fix-input-block { margin-bottom: 14px; }
.fix-input-block label {
  display: block; font-size: 11px; font-weight: 700; color: #94a3b8;
  margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;
}
.fix-hint-sum { font-size: 12px; color: #15803d; font-weight: 700; margin-top: 6px; }

.fix-preview {
  display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;
  margin-bottom: 14px;
}
.fp-cell {
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px;
  padding: 10px 12px; display: flex; flex-direction: column;
}
.fp-label { font-size: 10px; color: #94a3b8; font-weight: 700; text-transform: uppercase; }
.fp-val { font-size: 15px; font-weight: 800; margin-top: 4px; font-family: 'Inter', system-ui; }

.fix-note {
  font-size: 12px; color: #475569; background: #f1f5f9;
  border-radius: 10px; padding: 9px 12px; margin-bottom: 12px;
}
.fix-note.warn { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }

.fix-error {
  background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px;
  padding: 10px 14px; font-size: 13px; color: #b91c1c; margin-bottom: 12px; font-weight: 600;
}
.fix-done {
  background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px;
  padding: 10px 14px; font-size: 14px; color: #15803d; font-weight: 700; margin-bottom: 12px;
}
.fix-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 6px; }
.btn-secondary {
  background: #f1f5f9; color: #334155; border: none;
  padding: 9px 16px; border-radius: 10px; font-weight: 700; font-size: 14px; cursor: pointer;
}
.btn-secondary:hover { background: #e2e8f0; }
.btn-apply {
  background: linear-gradient(135deg, #2563eb, #1d4ed8); color: white; border: none;
  padding: 9px 18px; border-radius: 10px; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; gap: 6px; font-size: 14px;
  box-shadow: 0 4px 12px rgba(37,99,235,0.25);
}
.btn-apply:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; }

/* So'm/dollar tanlagich */
.cur-row { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.cur-row-label { font-size: 12px; font-weight: 700; color: #64748b; min-width: 62px; }
.fix-hint-sub { display: block; font-size: 11px; color: #94a3b8; font-weight: 600; margin-top: 2px; }
.cur-toggle { display: inline-flex; gap: 0; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
.cur-btn { border: none; background: #f8fafc; color: #64748b; padding: 7px 18px; font-weight: 700; font-size: 13px; cursor: pointer; transition: .15s; }
.cur-btn + .cur-btn { border-left: 1px solid #e2e8f0; }
.cur-btn.active { background: #2563eb; color: #fff; }
.fix-hint-cur { font-size: 12px; color: #b45309; font-weight: 700; margin-top: 6px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 8px; padding: 6px 10px; }
</style>
